import React, { useEffect, useState } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Spinner,
} from 'reactstrap';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import { get, post, del } from '../../helpers/api_helper';

function ItemProperties(props) {
  const {
    item,
    t,
  } = props;

  const [properties, setProperties] = useState([]);
  const [itemProperties, setItemProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingPropertyId, setSavingPropertyId] = useState(null);

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const getPropertyChoices = (prop) => {
    if (!prop?.choices) return [];

    try {
      const choices = Array.isArray(prop.choices) ? prop.choices : JSON.parse(prop.choices);

      return choices.map((choice) => `${choice}`.trim()).filter(Boolean);
    } catch (e) {
      toastr.error(e.message, t('Error while loading property choices'));
      return [];
    }
  };

  const getPropertyValue = (propertyId) => (
    itemProperties.find((itemProperty) => (
      `${itemProperty.property_id}` === `${propertyId}`
    ))?.value ?? ''
  );

  const getAllProperties = async (page = 1, loadedResults = []) => {
    const perPage = 100;
    const response = await get(`/api/fuse/property?page=${page}&sort=label&dir=asc&per=${perPage}`);

    if (Array.isArray(response)) {
      return response;
    }

    const pageResults = response.results || [];
    const results = loadedResults.concat(pageResults);
    const total = response.total || results.length;
    const pageCount = Math.ceil(total / perPage);

    if (pageResults.length === 0 || page >= pageCount) {
      return results;
    }

    return getAllProperties(page + 1, results);
  };

  const setProperty = async (propertyId, value) => {
    try {
      setSavingPropertyId(propertyId);
      await post(`/api/fuse/property/item/${item.id}/${propertyId}`, { value });

      setItemProperties((current) => {
        const existing = current.find((itemProperty) => (
          `${itemProperty.property_id}` === `${propertyId}`
        ));

        if (existing) {
          return current.map((itemProperty) => (
            `${itemProperty.property_id}` === `${propertyId}`
              ? { ...itemProperty, value }
              : itemProperty
          ));
        }

        return [...current, { property_id: propertyId, value }];
      });

      toastr.success('Property updated!', t('Success'));
    } catch (e) {
      toastr.error(e.message, t('Error setting Fuse property'));
    } finally {
      setSavingPropertyId(null);
    }
  };

  const deleteProperty = async (propertyId) => {
    try {
      setSavingPropertyId(propertyId);
      await del(`/api/fuse/property/item/${item.id}/${propertyId}`);

      setItemProperties((current) => current.filter((itemProperty) => (
        `${itemProperty.property_id}` !== `${propertyId}`
      )));

      setProperties(properties.filter((prop) => `${prop.id}` !== `${propertyId}`));

      toastr.success('Property deleted!', t('Success'));
    } catch (e) {
      toastr.error(e.message, t('Error deleting Fuse property'));
    } finally {
      setSavingPropertyId(null);
    }
  };

  useEffect(() => {
    if (!item?.id) {
      setLoading(false);
      return () => {};
    }
    let canceled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const itemPropertyResults = await get(`/api/fuse/property/item/${item.id}/properties`);

        if (canceled) {
          return;
        }

        if (itemPropertyResults.length === 0) {
          setProperties([]);
          setItemProperties([]);
          return;
        }

        const propertyResults = await getAllProperties();

        if (!canceled) {
          setProperties(propertyResults.filter((prop) => itemPropertyResults.some((itemProp) => (
            `${itemProp.property_id}` === `${prop.id}`
          ))));
          setItemProperties(itemPropertyResults);
        }
      } catch (e) {
        if (!canceled) {
          setError(e.message);
        }
      } finally {
        if (!canceled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      canceled = true;
    };
  }, [item?.id]);

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner type="border" color="info" />
      </div>
    );
  }

  if (error !== null) {
    return (
      <Alert color="danger" className="mt-3" isOpen>
        {error}
      </Alert>
    );
  }

  return (
    <table className="table table-bordered table-striped responsiveTable mb-0">
      <thead>
        <tr>
          <th>{t('Property')}</th>
          <th>{t('Value')}</th>
          <th>{t('Actions')}</th>
        </tr>
      </thead>
      <tbody>
        {properties.length > 0 && properties.map((property) => {
          const choices = getPropertyChoices(property);
          const currentValue = getPropertyValue(property.id);
          const selectOptions = currentValue !== '' && !choices.includes(currentValue)
            ? [currentValue, ...choices]
            : choices;

          return (
            <tr key={property.id}>
              <td className="align-middle">
                <div className="fw-semibold">{property.label}</div>
                {property.instructions && (
                  <div className="text-muted small">{property.instructions}</div>
                )}
              </td>
              <td className="align-middle">
                <select
                  className="form-control form-control-sm"
                  value={currentValue}
                  disabled={savingPropertyId === property.id || choices.length === 0}
                  onChange={(e) => setProperty(property.id, e.target.value)}
                >
                  <option value="">{t('Choose...')}</option>
                  {selectOptions.map((choice) => (
                    <option key={choice} value={choice}>
                      {choice}
                    </option>
                  ))}
                </select>
              </td>
              <td className="align-middle">

                <Button
                  title={t('Delete')}
                  color="danger"
                  aria-label={t('Delete')}
                  className="btn-soft-danger ms-2"
                  onClick={
                  () => {
                    // eslint-disable-next-line no-alert
                    if (window.confirm(t('Are you sure you want to delete this property from this item? This action cannot be undone.'))) {
                      deleteProperty(property.id);
                    }
                  }
}
                >
                  <span className="mdi mdi-trash-can" />
                </Button>
              </td>
            </tr>
          );
        })}

        {properties.length === 0 && (
          <tr>
            <td colSpan="3" className="text-muted">
              {t('No properties found.')}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

ItemProperties.propTypes = {
  item: PropTypes.object.isRequired,
  // eslint-disable-next-line react/require-default-props
  t: PropTypes.any,
};

export default connect(() => ({

}), {})(withTranslation()(ItemProperties));
